import { Link, useNavigate, useParams } from 'react-router-dom';
import React, { useMemo, useRef } from 'react';
import { Rating } from 'primereact/rating';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import IconEdit from '../../components/images/IconEdit.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import { useGetContactQuery, useUpdateContactPictureMutation } from '../../api/contact.js';
import IconEditContact from '../../components/images/IconEditContact.jsx';
import IconCalendar from '../../components/images/IconCalendar.jsx';
import IconEmail from '../../components/images/IconEmail.jsx';
import IconPhone from '../../components/images/IconPhone.jsx';
import CompanyCarousel from '../../components/misc/CompanyCarousel.jsx';
import IconAdd from '../../components/images/IconAdd.jsx';
import LanguageImage from '../../components/misc/LanguageImage.jsx';
import SocialImage from '../../components/misc/SocialImage.jsx';
import IconSocialAdd from '../../components/images/IconSocialAdd.jsx';
import IconArrowRight from '../../components/images/IconArrowRight.jsx';
import { useGetPoliticalAffiliationsQuery } from '../../api/misc.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { PERMISSION_CONTACTS } from '../../utils/constants.js';
import ModelShared from '../../components/misc/ModelShared.jsx';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import IconImageEdit from '../../components/images/IconImageEdit.jsx';
import ContactNews from '../../components/misc/ContactNews.jsx';
import { canUpdateOrDelete } from '../../utils/user.js';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Badge } from 'primereact/badge';

const ViewContact = () => {
    let contact = null;
    const { permissions: userPermissions } = useCurrentUser();
    const { id } = useParams();
    const user = useSelector((state) => state.user.user);
    const navigate = useNavigate();
    const fileInputRef = useRef();

    const { data: contactResponse } = useGetContactQuery(id);
    const [updateContactPicture] = useUpdateContactPictureMutation();

    contact = useMemo(() => {
        if (contactResponse?.response?.contact) {
            return contactResponse.response.contact;
        }

        return null;
    }, [contactResponse]);

    const { data: politicalAffiliationsData } = useGetPoliticalAffiliationsQuery({});
    const politicalAffiliations = useMemo(() => {
        if (politicalAffiliationsData?.response?.political_affiliations) {
            return politicalAffiliationsData.response.political_affiliations;
        }

        return [];
    }, [politicalAffiliationsData]);

    const handleAvatarChange = (event) => {
        const file = event.target.files?.[0];
        if (file && contact?.id) {
            updateContactPicture({ picture: file, id: contact.id });
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <AuthLayout
            title='View Contact'
            forwardRoute={
                contact && canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'update')
                    ? `/contacts/edit/${id}`
                    : null
            }
            forwardIcon={IconEdit}
            forwardPermission={PERMISSION_CONTACTS}
            forwardPermissionAttribute='update'
            forwardIconClassName='stroke-theme1-white w-6 h-6'
            backRoute='/contacts/list'
        >
            <div className='max-w-3xl w-full mx-auto px-8'>
                <BaseCard className='p-0'>
                    <div className='flex flex-row items-center px-8 py-6'>
                        <div className='mr-4 relative'>
                            {contact && (
                                <div className='rounded-full w-20 h-20 overflow-hidden'>
                                    {contact.avatar ? (
                                        <img src={contact.avatar} alt='Avatar' />
                                    ) : (
                                        <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                            <div className='text-theme1-primary font-bold text-3xl'>
                                                {`${contact.first_name?.[0]?.toUpperCase() || ''}${
                                                    contact.last_name?.[0]?.toUpperCase() || ''
                                                }`}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {!!userPermissions[PERMISSION_CONTACTS]?.update &&
                                contact &&
                                canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'update') && (
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className='absolute top-0 right-[-15px] cursor-pointer'
                                    >
                                        <IconImageEdit className='w-6 h-auto fill-theme1-dark' />
                                        <input
                                            type='file'
                                            ref={fileInputRef}
                                            className='hidden'
                                            onChange={handleAvatarChange}
                                            accept='image/*'
                                        />
                                    </div>
                                )}
                        </div>
                        <div className='flex-1'>
                            <div className='flex items-center'>
                                {contact && (
                                    <div className='text-theme1-primary font-bold text-[22px] mr-1'>
                                        {contact.first_name} {contact.middle_name} {contact.last_name}
                                    </div>
                                )}
                                {!!userPermissions[PERMISSION_CONTACTS]?.update &&
                                    contact &&
                                    canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'update') && (
                                        <div
                                            className='ml-auto cursor-pointer'
                                            onClick={() => navigate(`/contacts/edit/${id}`)}
                                        >
                                            <IconEditContact className='w-6 h-6 stroke-theme1-text1' />
                                        </div>
                                    )}
                            </div>
                            {contact && contact.companies?.length > 0 && (
                                <div className='text-sm text-theme1-primary2 font-semibold mt-1'>
                                    {contact.companies?.[0].job_title}
                                </div>
                            )}
                            {contact && contact.companies?.length > 0 && (
                                <div className='mt-1 text-base font-bold text-theme1-text2'>
                                    {contact.companies?.[0].name}
                                </div>
                            )}
                        </div>
                    </div>
                    <hr className='mb-6 mx-8 border-theme1-cardStroke' />

                    {contact && (
                        <div className='w-full mb-6 px-8'>
                            <div className='text-lg font-bold text-theme1-text2 mb-4'>
                                Shared within <span className='text-theme1-brightRed'>*</span>
                            </div>

                            <ModelShared
                                shared={contact.shared}
                                sharedUsers={contact.shared_users}
                                sharedOrganizations={contact.shared_organizations}
                                user={user}
                            />
                        </div>
                    )}

                    <div className='mx-8 text-theme1-text1 font-bold mb-4 flex items-center'>
                        <div className='mr-2'>Political View:</div>
                        <div className='font-bold'>
                            {
                                politicalAffiliations.find(
                                    (obj) => obj.value === contact?.details?.political_affiliation
                                )?.label
                            }
                        </div>
                    </div>
                    <div className='mx-8 text-theme1-text1 font-bold flex items-center'>
                        <div className='mr-2'>Reputation:</div>
                        <Rating
                            cancel={false}
                            value={contact?.details?.reputation}
                            disabled
                            onIcon={
                                <svg
                                    width='16'
                                    height='16'
                                    viewBox='0 0 44 43'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path
                                        d='M21.6157 3.5835L27.152 14.7993L39.5324 16.6089L30.574 25.3343L32.6882 37.661L21.6157 31.8381L10.5432 37.661L12.6574 25.3343L3.69904 16.6089L16.0795 14.7993L21.6157 3.5835Z'
                                        fill='#FFB421'
                                        stroke='#FFB421'
                                        strokeWidth='3.58333'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                            }
                            offIcon={
                                <svg
                                    width='16'
                                    height='16'
                                    viewBox='0 0 44 43'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path
                                        d='M21.6157 3.5835L27.152 14.7993L39.5324 16.6089L30.574 25.3343L32.6882 37.661L21.6157 31.8381L10.5432 37.661L12.6574 25.3343L3.69904 16.6089L16.0795 14.7993L21.6157 3.5835Z'
                                        fill='#D4C7C7'
                                        stroke='#D4C7C7'
                                        strokeWidth='3.58333'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                            }
                        />
                    </div>
                    <BaseCard className='bg-theme1-pinkGray mb-0 border-0 border-t border-theme1-cardStroke'>
                        <div className='font-bold text-theme1-text1 text-lg'>View Personal Info</div>
                        <hr className='my-4 border-theme1-icons' />
                        <div>
                            <div className='flex items-center mb-4'>
                                <IconEmail className='fill-theme1-text1 mr-4' />
                                <span className='text-theme1-text1'>{contact?.email}</span>
                            </div>
                            <div className='flex items-center mb-4'>
                                <IconPhone className='fill-theme1-text1 mr-4' />
                                <span className='text-theme1-text1'>{contact?.phone}</span>
                            </div>
                            <div className='flex items-center'>
                                {contact?.country?.code && (
                                    <LanguageImage
                                        alt={contact?.country?.code || ''}
                                        code={contact?.country?.code}
                                        className='mr-4 w-6 h-auto'
                                    />
                                )}
                                <span className='text-theme1-text1'>{contact?.country?.name}</span>
                            </div>
                        </div>
                        {contact?.social_links && contact?.social_links?.length > 0 && (
                            <div className='flex items-center justify-between mt-4'>
                                <div className='flex items-center'>
                                    {contact?.social_links?.map((social_link, index) => {
                                        return (
                                            <a key={index} href={social_link.url} target='_blank' rel='noreferrer'>
                                                <SocialImage
                                                    alt={social_link.type}
                                                    social={social_link.type}
                                                    className='w-6 h-6 mr-4'
                                                />
                                            </a>
                                        );
                                    })}
                                </div>
                                {!!userPermissions[PERMISSION_CONTACTS]?.update &&
                                    contact &&
                                    canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'update') && (
                                        <div onClick={() => navigate(`/contacts/edit/${id}`)}>
                                            <IconSocialAdd className='w-6 h-6' />
                                        </div>
                                    )}
                            </div>
                        )}
                    </BaseCard>
                </BaseCard>
                <div className='flex justify-between items-center mb-4'>
                    <div className='text-lg text-theme1-title2 font-bold'>Company Affiliations</div>
                    {!!userPermissions[PERMISSION_CONTACTS]?.update &&
                        contact &&
                        canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'update') && (
                            <div className='cursor-pointer' onClick={() => navigate(`/contacts/edit/${id}`)}>
                                <IconAdd className='w-8 h-8 fill-theme1-dark' />
                            </div>
                        )}
                </div>
                <CompanyCarousel items={contact?.companies || []} className='mb-6' />
            </div>

            <div className='max-w-3xl w-full mx-auto px-8'>
                <div
                    className={`${contact?.news_enabled ? 'mb-6' : 'mb-10'} border border-theme1-text3 p-4 rounded-2xl`}
                >
                    {contact?.news_enabled ? (
                        <>
                            <div className='text-lg font-bold text-theme1-primary mb-2'>News enabled</div>
                            <div className='flex gap-2 mb-2'>
                                <div className='text-theme1-text1 font-bold'>Frequency:</div>
                                <div>
                                    {contact?.news_frequency?.charAt(0).toUpperCase() +
                                        contact?.news_frequency?.slice(1)}
                                </div>
                            </div>
                            <div className='flex gap-2'>
                                <div className='text-theme1-text1 font-bold'>Keywords:</div>
                                <div className='flex flex-wrap'>
                                    {contact?.news_keywords.map((keyword, index, arr) => (
                                        <span key={keyword} className='mr-2'>
                                            {keyword}
                                            {index < arr.length - 1 ? ', ' : ''}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className='text-theme1-primary'>News are not enabled for this contact</div>
                    )}
                </div>
            </div>

            <ContactNews contactId={id} newsEnabled={contact?.news_enabled} />

            {(contactResponse?.response?.next_meetings?.length > 0 ||
                contactResponse?.response?.previous_meetings?.length > 0) && (
                <div className='bg-theme1-primary2 mt-6 text-white pt-4 max-w-3xl w-full mx-auto'>
                    <>
                        {contactResponse?.response?.next_meetings?.length > 0 && (
                            <div className='max-w-3xl w-full mx-auto px-8 mb-5'>
                                <div className='text-xl font-bold mb-4'>Next Meetings</div>
                                {contactResponse.response.next_meetings.map((meeting) => (
                                    <div key={meeting.id}>
                                        <Link
                                            to={`/calendar/view/${meeting.id}`}
                                            className='flex items-start py-4 border-t border-solid border-theme1-tanTransparent'
                                        >
                                            <div className='mr-4'>
                                                <IconCalendar className='w-6 h-6 stroke-white' />
                                            </div>

                                            <div className='flex-grow'>
                                                <div className='text-base'>
                                                    <span className='font-bold'>Date: </span>
                                                    {meeting.all_day
                                                        ? 'All day meeting'
                                                        : `${dayjs(meeting.start_date).format('DD.MM.YYYY')}, ${dayjs(meeting.start_time).format('HH:mm')} - ${dayjs(meeting.end_date).format('DD.MM.YYYY')}, ${dayjs(meeting.end_time).format('HH:mm')}`}
                                                </div>
                                                <div className='mt-1 text-base'>
                                                    <span className='font-bold'>Title: </span>
                                                    {meeting.title}
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        )}

                        {contactResponse?.response?.previous_meetings?.length > 0 && (
                            <div className='max-w-3xl w-full mx-auto px-8'>
                                <div className='text-xl font-bold mb-4'>Previous Meetings</div>
                                {contactResponse.response.previous_meetings.map((meeting) => (
                                    <div key={meeting.id}>
                                        <Link
                                            to={`/calendar/view/${meeting.id}`}
                                            className='flex items-start py-4 border-t border-solid border-theme1-tanTransparent'
                                        >
                                            <div className='mr-4'>
                                                <IconCalendar className='w-6 h-6 stroke-white' />
                                            </div>

                                            <div className='flex-grow'>
                                                <div className='text-base'>
                                                    <span className='font-bold'>Date: </span>
                                                    {meeting.all_day
                                                        ? 'All day meeting'
                                                        : `${dayjs(meeting.start_date).format('DD.MM.YYYY')}, ${dayjs(meeting.start_time).format('HH:mm')} - ${dayjs(meeting.end_date).format('DD.MM.YYYY')}, ${dayjs(meeting.end_time).format('HH:mm')}`}
                                                </div>
                                                <div className='mt-1 text-base'>
                                                    <span className='font-bold'>Title: </span>
                                                    {meeting.title}
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                </div>
            )}

            <div className='max-w-3xl w-full mx-auto px-8 mt-8 mb-10'>
                <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 mt-0'>
                    <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Needs & Skills</div>
                        {!!userPermissions[PERMISSION_CONTACTS]?.update &&
                            contact &&
                            canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'update') && (
                                <div onClick={() => navigate(`/contacts/edit/${id}`)}>
                                    <IconArrowRight className='w-8 h-8 stroke-theme1-title2' />
                                </div>
                            )}
                    </div>
                    <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='needs-tab'
                                    className={`custom-accordion-tab-no-padding ${!contact?.needs?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Needs</span>
                                            <Badge value={contact?.needs?.length} className='ns-badge'></Badge>
                                        </>
                                    }
                                    disabled={!contact?.needs?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {contact?.needs &&
                                            contact?.needs.map((need) => (
                                                <span
                                                    key={need.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {need.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                    <BaseCard className='border-0 m-0 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion'>
                                <AccordionTab
                                    key='capabilities-tab'
                                    className={`custom-accordion-tab-no-padding ${!contact?.skills?.length ? 'custom-accordion-tab-disabled' : ''}`}
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Skills</span>
                                            <Badge value={contact?.skills?.length} className='ns-badge'></Badge>
                                        </>
                                    }
                                    disabled={!contact?.skills?.length}
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {contact?.skills &&
                                            contact?.skills.map((skill) => (
                                                <span
                                                    key={skill.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    {skill.name}
                                                </span>
                                            ))}
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                </BaseCard>

                {!!userPermissions[PERMISSION_CONTACTS]?.read && contact?.files?.length > 0 && (
                    <BaseCard className='border-0 bg-theme1-tan p-0 pb-6 max-w-3xl w-full mx-auto'>
                        <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                            <div className='text-2xl font-bold text-theme1-title2'>Files</div>
                        </div>
                        <BaseCard className='border-0 m-0 py-0 px-4 mx-6 w-auto'>
                            <div className='max-w-3xl w-full mx-auto px-2 pb-4'>
                                {contact.files.map((file) => {
                                    return (
                                        <div key={file.id} className='w-full flex pt-4 items-center'>
                                            <a className='flex-grow text-sm text-theme1-text1' href={file.url}>
                                                {file.title}
                                            </a>
                                        </div>
                                    );
                                })}
                            </div>
                        </BaseCard>
                    </BaseCard>
                )}

                {contact?.notes.length > 0 && (
                    <BaseCard className='border-0 bg-theme1-pinkGray p-0 pb-6 max-w-3xl w-full mx-auto'>
                        <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                            <div className='text-2xl font-bold text-theme1-title2'>Notes</div>
                            {!!userPermissions[PERMISSION_CONTACTS]?.update &&
                                contact &&
                                canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'update') && (
                                    <div onClick={() => navigate(`/contacts/edit/${id}`)}>
                                        <IconArrowRight className='w-8 h-8 stroke-theme1-title2' />
                                    </div>
                                )}
                        </div>
                        <BaseCard className='border-0 m-0 py-0 px-4 mx-6 w-auto'>
                            <div className='max-w-3xl w-full mx-auto px-2 pb-4'>
                                {contact.notes.map((note) => {
                                    return (
                                        <div key={note.id} className='w-full flex pt-4 items-center'>
                                            <div className='text-theme1-primary font-bold text-xs mr-4'>
                                                {dayjs(note.created_at).format('DD.MM.YYYY')}
                                            </div>
                                            <div className='flex-grow text-sm text-theme1-text1'>{note.note}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </BaseCard>
                    </BaseCard>
                )}
            </div>

            {contact?.user && (
                <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between mb-6'>
                    <div className='text-lg font-bold text-theme1-title2'>
                        <div>Added By</div>
                        <div className='text-base text-theme1-primary mt-1'>
                            <Link to={`/contacts/view/${contact.user.contact_id}`}>{contact?.user.name}</Link>
                        </div>
                    </div>
                </div>
            )}
        </AuthLayout>
    );
};

export default ViewContact;
